import React, { useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { getMemberMemos, addMemberMemo, updateMemberMemoById, deleteMemberMemo } from '../../../api/user';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonSelect from '../../../form-components/CommonSelect';
import { getBranchManager } from '../../../api/staff';
import {
  MemoModalTextarea,
  MemoInputSection,
  MemoList,
  MemoItem,
  MemoItemActions,
  MemoActionButton,
  MemoContent,
  MemoItemMeta,
} from '../UserDetail/UserDetailStyled';
import { SubsectionLabel } from './UserGeneralTabStyled';

const MEMO_MAX_LENGTH = 2000;

const formatDate = dateStr => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '.').slice(0, 16);
};

const MemberMemoSection = ({ t, user, bigUser, branchId }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const memberId = user?.memberId;
  const token = bigUser?.token;

  const [newMemo, setNewMemo] = useState('');
  const [newManagerId, setNewManagerId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  const invalidateMemos = () => {
    queryClient.invalidateQueries({ queryKey: ['memberMemos', memberId, branchId] });
    queryClient.invalidateQueries({ queryKey: MEMBER_ADDITION_INFO_KEY(memberId, branchId) });
  };

  const { data: memos = [] } = useQuery({
    queryKey: ['memberMemos', memberId, branchId],
    queryFn: () => getMemberMemos(memberId, branchId, token),
    enabled: !!memberId && !!branchId && !!token,
  });

  // 담당자는 강사만이 아니라 이 지점 재직중 임직원 전체다.
  const { data: managers = [] } = useQuery({
    queryKey: ['branchManagers', branchId],
    queryFn: () => getBranchManager(branchId, token),
    enabled: !!branchId && !!token,
  });

  // 메모의 managerId 는 Employee ID 공간이라 managerId(User ID)가 아니라 employeeId 를 보낸다.
  const managerOptions = managers.map(manager => ({
    value: manager.employeeId,
    label: manager.managerName,
  }));

  const addMutation = useMutation({
    mutationFn: () => addMemberMemo(memberId, { branchId, memo: newMemo, managerId: newManagerId || null }, token),
    onSuccess: () => {
      toast.success(t('메모가 추가되었습니다'));
      setNewMemo('');
      setNewManagerId('');
      invalidateMemos();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ memoId, memo }) => updateMemberMemoById(memberId, memoId, { branchId, memo }, token),
    onSuccess: () => {
      toast.success(t('메모가 수정되었습니다'));
      setEditingId(null);
      setEditingText('');
      invalidateMemos();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: memoId => deleteMemberMemo(memberId, memoId, token),
    onSuccess: () => {
      toast.success(t('메모가 삭제되었습니다'));
      setDeleteTarget(null);
      invalidateMemos();
    },
  });

  const handleAdd = () => {
    if (!newMemo.trim()) {
      toast.error(t('메모를 입력해 주세요.'));
      return;
    }
    addMutation.mutate();
  };

  const handleEditStart = memo => {
    setEditingId(memo.id);
    setEditingText(memo.memo);
  };

  const handleEditSave = () => {
    if (!editingText.trim()) {
      toast.error(t('메모를 입력해 주세요.'));
      return;
    }
    updateMutation.mutate({ memoId: editingId, memo: editingText.trim() });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {/* 라벨 행: 왼쪽 "메모" | 오른쪽 "메모 목록" 줄 맞춤 */}
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ flex: '0 0 45%', pr: '20px' }}>
          <SubsectionLabel variant="caption">{t('메모')}</SubsectionLabel>
        </Box>
        <Box sx={{ flex: 1, borderLeft: '1px solid #e0e0e0', pl: '24px' }}>
          <SubsectionLabel variant="caption">{t('메모 목록')} ({memos.length})</SubsectionLabel>
        </Box>
      </Box>

      {/* 모달과 동일한 레이아웃: 왼쪽 입력 | 구분선 | 오른쪽 리스트 */}
      <Box sx={{ display: 'flex', gap: '0', height: '320px', overflow: 'hidden' }}>
        {/* 왼쪽: 메모 입력 */}
        <MemoInputSection sx={{ flex: '0 0 45%', pr: '20px' }}>
          <MemoModalTextarea
            value={newMemo}
            maxLength={MEMO_MAX_LENGTH}
            placeholder={t('새 메모를 입력해 주세요.')}
            onChange={e => setNewMemo(e.target.value)}
            style={{ flex: 1, resize: 'none', fontSize: '13px' }}
          />
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <CommonSelect
              value={newManagerId}
              onChange={e => setNewManagerId(e.target.value)}
              options={managerOptions}
              emptyLabel="담당자 선택"
              width={160}
            />
            <CommonModalButton variant="submit" onClick={handleAdd} disabled={addMutation.isPending}
              sx={{ height: '36px', fontSize: '13px', minWidth: '56px', ml: 'auto' }}>
              {t('추가')}
            </CommonModalButton>
          </Box>
        </MemoInputSection>

        {/* 오른쪽: 메모 리스트 */}
        <MemoList sx={{ height: '100%', overflowY: 'auto' }}>
          {memos.length === 0 && (
            <MemoItemMeta sx={{ textAlign: 'center', py: '24px' }}>{t('등록된 메모가 없습니다.')}</MemoItemMeta>
          )}
          {memos.map(memo => (
            <MemoItem key={memo.id}>
              {editingId === memo.id ? (
                <>
                  <MemoModalTextarea
                    minRows={3}
                    maxRows={8}
                    value={editingText}
                    maxLength={MEMO_MAX_LENGTH}
                    onChange={e => setEditingText(e.target.value)}
                    style={{ width: '100%', fontSize: '13px' }}
                  />
                  <MemoItemActions>
                    <MemoActionButton variant="save" onClick={handleEditSave} disabled={updateMutation.isPending}>
                      저장
                    </MemoActionButton>
                    <MemoActionButton variant="cancel" onClick={() => { setEditingId(null); setEditingText(''); }}>
                      취소
                    </MemoActionButton>
                  </MemoItemActions>
                </>
              ) : (
                <>
                  <MemoContent>{memo.memo}</MemoContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '10px', pt: '8px', borderTop: '1px solid #eee' }}>
                    <MemoItemActions sx={{ m: 0, p: 0, border: 'none' }}>
                      <MemoActionButton variant="edit" onClick={() => handleEditStart(memo)}>
                        수정
                      </MemoActionButton>
                      <MemoActionButton variant="delete" onClick={() => setDeleteTarget(memo.id)}>
                        삭제
                      </MemoActionButton>
                    </MemoItemActions>
                    <MemoItemMeta sx={{ m: 0 }}>
                      <span>담당자: {memo.createdByName || '-'}</span>
                      <span style={{ margin: '0 6px' }}>/</span>
                      <span>등록일: {formatDate(memo.createdAt)}</span>
                    </MemoItemMeta>
                  </Box>
                </>
              )}
            </MemoItem>
          ))}
        </MemoList>
      </Box>

      {/* 삭제 확인 */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>{t('메모 삭제')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#333' }}>{t('이 메모를 삭제하시겠습니까?')}</Typography>
          <Typography sx={{ fontSize: '13px', color: '#999', mt: '6px' }}>{t('삭제된 메모는 복구할 수 없습니다.')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={() => setDeleteTarget(null)}>{t('취소')}</CommonModalButton>
          <CommonModalButton variant="delete" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {t('삭제')}
          </CommonModalButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberMemoSection;
